import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

type TourFlags = Record<string, boolean>;

interface UseTourFlagsOptions {
  /**
   * Key prefix used for localStorage caching to allow fast reads before the
   * remote flags arrive.
   */
  storagePrefix?: string;
}

interface UseTourFlagsResult {
  flags: TourFlags;
  loading: boolean;
  /**
   * Returns true once we have attempted to fetch the flags at least once.
   */
  initialized: boolean;
  /**
   * Mark a specific tour flag as seen. This updates local state immediately
   * and schedules a POST to the backend. Repeated calls are deduped.
   */
  markSeen: (flag: string) => Promise<void>;
  /**
   * Imperatively refresh the flags from the backend.
   */
  refresh: () => Promise<void>;
}

const DEFAULT_PREFIX = 'tourFlags';

export function useTourFlags(
  userId: string | null | undefined,
  apiBase: string,
  { storagePrefix = DEFAULT_PREFIX }: UseTourFlagsOptions = {}
): UseTourFlagsResult {
  const [flags, setFlags] = useState<TourFlags>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const inflightUpdate = useRef<Set<string>>(new Set());
  const storageKey = useMemo(
    () => (userId ? `${storagePrefix}:${userId}` : null),
    [storagePrefix, userId]
  );

  // Hydrate from localStorage first so we can make immediate decisions while
  // waiting for the backend result.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setFlags(parsed);
      }
    } catch {
      // Ignore JSON parse errors - we will refresh from the backend next.
    }
  }, [storageKey]);

  const persistLocal = useCallback(
    (next: TourFlags) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Storage is best-effort. Failure is non-fatal.
      }
    },
    [storageKey]
  );

  const refresh = useCallback(async () => {
    if (!userId) {
      setFlags({});
      setInitialized(true);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/user/ui-flags`, {
        params: { userId },
      });
      const remoteFlags: TourFlags =
        (response.data?.uiFlags as TourFlags | undefined) ?? {};
      setFlags(remoteFlags);
      persistLocal(remoteFlags);
    } catch (_err) {
      // Preserve existing flags on failure; caller can retry later.
    } finally {
      setInitialized(true);
      setLoading(false);
    }
  }, [apiBase, persistLocal, userId]);

  useEffect(() => {
    if (!initialized && !loading) {
      void refresh();
    }
  }, [initialized, loading, refresh]);

  const markSeen = useCallback(
    async (flag: string) => {
      if (!flag) return;
      setFlags(prev => {
        if (prev[flag]) {
          return prev;
        }
        const next = { ...prev, [flag]: true };
        persistLocal(next);
        return next;
      });
      if (!userId) return;

      const key = `${userId}:${flag}`;
      if (inflightUpdate.current.has(key)) {
        return;
      }
      inflightUpdate.current.add(key);
      try {
        await axios.post(`${apiBase}/user/ui-flags/set`, {
          userId,
          flags: { [flag]: true },
        });
      } catch (_err) {
        // If the write fails we leave the optimistic flag in place so the user
        // is not nagged again. The next successful refresh will realign state.
      } finally {
        inflightUpdate.current.delete(key);
      }
    },
    [apiBase, persistLocal, userId]
  );

  return {
    flags,
    loading,
    initialized,
    markSeen,
    refresh,
  };
}
